<template>
	<section
		class="intro-block"
		:class="{
			'no-top-image': !item.imageTopLeft && !item.imageTopMiddle && !item.imageTopRight,
			'no-bottom-image': !item.imageBottomLeft && !item.imageBottomRight,
		}"
	>
		<picture v-if="item.imageTopLeft" class="picture-top-left">
			<source :srcset="item.imageTopLeftWebp" type="image/webp" />
			<source :srcset="item.imageTopLeft" />
			<img
				:src="item.imageTopLeft"
				:alt="item.imageTopLeftAlt"
				class="intro-block-image top-left fade-animate fade-right"
				loading="lazy"
			/>
		</picture>

		<picture v-if="item.imageTopMiddle" class="picture-top-middle">
			<source :srcset="item.imageTopMiddleWebp" type="image/webp" />
			<source :srcset="item.imageTopMiddle" />
			<img
				:src="item.imageTopMiddle"
				:alt="item.imageTopMiddleAlt"
				class="intro-block-image top-middle fade-animate fade-up"
				loading="lazy"
			/>
		</picture>

		<picture v-if="item.imageTopRight" class="picture-top-right">
			<source :srcset="item.imageTopRightWebp" type="image/webp" />
			<source :srcset="item.imageTopRight" />
			<img
				:src="item.imageTopRight"
				:alt="item.imageTopRightAlt"
				class="intro-block-image top-right fade-animate fade-left"
				loading="lazy"
			/>
		</picture>

		<div class="row">
			<div class="columns column12 align-center">
				<div class="content">
					<h1>{{ item.header }}</h1>
					<div v-parse-links v-html="item.content" />
				</div>
			</div>
		</div>

		<picture v-if="item.imageBottomLeft" class="picture-bottom-left">
			<source :srcset="item.imageBottomLeftWebp" type="image/webp" />
			<source :srcset="item.imageBottomLeft" />
			<img
				:src="item.imageBottomLeft"
				:alt="item.imageBottomLeftAlt"
				class="intro-block-image bottom-left fade-animate fade-right"
				loading="lazy"
			/>
		</picture>

		<picture v-if="item.imageBottomRight" class="picture-bottom-right">
			<source :srcset="item.imageBottomRightWebp" type="image/webp" />
			<source :srcset="item.imageBottomRight" />
			<img
				:src="item.imageBottomRight"
				:alt="item.imageBottomRightAlt"
				class="intro-block-image bottom-right fade-animate fade-left"
				loading="lazy"
			/>
		</picture>
	</section>
</template>

<script setup>
defineProps({
	item: {
		type: Object,
		default: () => ({
			imageTopMiddle: { type: String, default: '' },
			imageTopLeft: { type: String, default: '' },
			imageTopRight: { type: String, default: '' },
			imageBottomLeft: { type: String, default: '' },
			imageBottomRight: { type: String, default: '' },
			header: { type: String, default: '' },
			content: { type: String, default: '' },
		}),
	},
});
</script>

<style lang="scss" scoped>
.no-heading-image {
	.intro-block {
		margin: 50px auto;
		padding: 300px 0;
		position: relative;
		max-width: 1800px;
		overflow: hidden;

		&.no-top-image {
			padding-top: 100px;
		}

		&.no-bottom-image {
			padding-bottom: 100px;
		}
	}
}

.intro-block {
	margin: 50px auto;
	padding: 300px 0;
	position: relative;
	max-width: 1800px;
	overflow: hidden;

	&.no-top-image {
		padding-top: 50px;
		margin-top: 50px;
	}

	&.no-bottom-image {
		padding-bottom: 100px;
	}

	.row {
		position: relative;
		z-index: 1;
	}

	.column12 {
		width: 94%;
		max-width: 1620px;
		margin: 0 auto;
		background: var(--intro-block-background-color);
		padding: 30px;
	}

	.content {
		text-align: center;
		max-width: 740px;
		margin: 100px auto;
	}

	:deep(a.no-fill) {
		color: var(--body-color);
		border-color: #1d1d1c;

		&:hover {
			border-color: var(--cta-color);
		}

		&:active {
			color: #fff;
		}
	}
}

.intro-block-image {
	max-width: 372px;
	position: absolute;
	z-index: 2;

	&.top-left {
		left: 10px;
		top: 140px;
	}

	&.top-middle {
		top: 0;
		left: 0;
		right: 0;
		margin: 0 auto;
	}

	&.top-right {
		right: 110px;
		top: 90px;
	}

	&.bottom-left {
		left: 90px;
		bottom: 0;
		max-width: 320px;
	}

	&.bottom-right {
		right: 0;
		bottom: 120px;
	}
}

@media (max-width: 1500px) {
	.intro-block-image {
		max-width: 300px;

		&.bottom-left {
			max-width: 300px;
		}

		&.top-middle {
			top: 40px;
		}
	}
}

@media (max-width: 1380px) {
	.intro-block {
		padding: 240px 0;
	}

	.intro-block-image {
		&.top-left {
			left: 5px;
			top: 100px;
		}

		&.top-middle {
			top: 20px;
			left: 0;
			right: 0;
			margin: 0 auto;
		}

		&.top-right {
			right: 40px;
			top: 70px;
		}

		&.bottom-left {
			left: 60px;
			bottom: 0;
		}

		&.bottom-right {
			right: 0;
			bottom: 70px;
		}
	}
}

@media (max-width: 1080px) {
	.intro-block-image {
		max-width: 260px;

		&.bottom-left {
			max-width: 260px;
		}
	}
}

@media (max-width: 880px) {
	.no-heading-image .intro-block,
	.intro-block {
		text-align: center;
		padding: 30px 0 270px;

		&.no-top-image {
			padding-top: 0;
			margin-top: 0;
		}

		&.no-bottom-image {
			padding-bottom: 0;
			margin-bottom: 0;
		}

		picture {
			display: block;
			width: 100%;
		}

		.picture-bottom-left {
			text-align: left;
			margin-left: 40px;
		}

		.picture-bottom-right {
			text-align: right;
			margin-right: 40px;

			img {
				margin: 20px 40px 0 0;
			}
		}
	}

	.intro-block-image {
		position: relative;
		margin: 20px auto;

		&.top-middle,
		&.top-right,
		&.top-left,
		&.bottom-left,
		&.bottom-right {
			top: unset;
			left: unset;
			right: unset;
			bottom: unset;
		}

		&.top-left {
			position: absolute;
			bottom: 0;
			left: 40px;
			right: auto;
			margin-left: auto;
			margin-right: auto;
		}
	}
}
</style>
